import { G } from '@mobily/ts-belt';
import { isAfter, isEqual } from 'date-fns';
import { RefinementCtx, z } from 'zod';

import { B2bCampaignTypePersonalAssetsGroup } from '@Common/bo/campaign_personal_assets';
import {
  ZodB2bSavingPlanCategory,
  ZodB2bSavingPlanDeductibility,
  ZodB2bSavingPlanType,
} from '@Common/types/b2b/plans';
import { ZodB2bCashSource } from '@Common/types/bo/b2b/b2b';
import {
  type B2bCampaignActionHandler,
  ZodB2bCampaignActionHandler,
  ZodB2bContractType,
} from '@Common/types/bo/b2b/contracts';
import { ZodTaxResidenceCode } from '@Common/types/bo/b2b/employees';
import { BoListQueryBase } from '@Common/types/bo/bo_list_query';
import { ZodBoExportResponse } from '@Common/types/bo/exports';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import { ZodDate } from '@Common/types/date';
import { ZodAmountInDays } from '@Common/types/explore/employee_savings_campaign';
import { ZodEmployeeSavingsRiskProfile } from '@Common/types/profile/employee_savings';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodDateString, ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const B2bCampaignPaymentMethodValues = ['sepa', 'transfer'] as const;
export const ZodB2bCampaignPaymentMethod = z.enum(B2bCampaignPaymentMethodValues);
export type B2bCampaignPaymentMethod = z.TypeOf<typeof ZodB2bCampaignPaymentMethod>;
export const B2bCampaignStatusValues = [
  'campaign_draft',
  'campaign_draft_failed',
  'campaign_published',
  'campaign_published_failed',
  'campaign_started',
  'campaign_started_failed',
  'campaign_ended',
  'campaign_ended_failed',
  'campaign_cancelled',
  'campaign_cancelled_failed',
  'campaign_investment_amount',
  'campaign_investment_amount_failed',
  'company_funds_await',
  'company_funds_await_failed',
  'company_funds_received',
  'company_funds_received_failed',
  'finished',
  'finished_failed',
  'campaign_ending_grace_period',
  'campaign_ending_grace_period_failed',
  'campaign_company_allocation_choices_processed',
  'campaign_company_allocation_choices_processed_failed',
  'campaign_pending_allocation_computation',
  'campaign_pending_allocation_computation_failed',
  'campaign_awaiting_customer_confirmation',
  'campaign_awaiting_customer_confirmation_failed',
  'campaign_unused_vacation_days_cash_conversion',
  'campaign_unused_vacation_days_cash_conversion_failed',
] as const;

export const ZodB2bCampaignStatus = z.enum(B2bCampaignStatusValues);
export type B2bCampaignStatus = z.TypeOf<typeof ZodB2bCampaignStatus>;

export type B2bCampaignTab = 'draft' | 'in_progress' | 'history';

/** Which tab of the BO campaign list a status is listed under. */
export const B2bCampaignStatusTabMapping: Record<B2bCampaignStatus, B2bCampaignTab> = {
  campaign_draft: 'draft',
  campaign_draft_failed: 'draft',
  campaign_published: 'in_progress',
  campaign_published_failed: 'in_progress',
  campaign_started: 'in_progress',
  campaign_started_failed: 'in_progress',
  campaign_ended: 'in_progress',
  campaign_ended_failed: 'in_progress',
  campaign_cancelled: 'history',
  campaign_cancelled_failed: 'history',
  campaign_investment_amount: 'in_progress',
  campaign_investment_amount_failed: 'in_progress',
  company_funds_await: 'in_progress',
  company_funds_await_failed: 'in_progress',
  company_funds_received: 'in_progress',
  company_funds_received_failed: 'in_progress',
  finished: 'history',
  finished_failed: 'history',
  campaign_ending_grace_period: 'in_progress',
  campaign_ending_grace_period_failed: 'in_progress',
  campaign_unused_vacation_days_cash_conversion: 'in_progress',
  campaign_unused_vacation_days_cash_conversion_failed: 'in_progress',
  campaign_company_allocation_choices_processed: 'in_progress',
  campaign_company_allocation_choices_processed_failed: 'in_progress',
  campaign_pending_allocation_computation: 'draft',
  campaign_pending_allocation_computation_failed: 'draft',
  campaign_awaiting_customer_confirmation: 'draft',
  campaign_awaiting_customer_confirmation_failed: 'draft',
};

export const isB2bCampaignInProgress = (status: B2bCampaignStatus | null): boolean =>
  status !== null && B2bCampaignStatusTabMapping[status] === 'in_progress';

// Completed, for the plan-retractation campaign selector (LIMA-740): the whole 'history' bucket,
// derived from the mapping above so it can't drift from it.
export const B2B_CAMPAIGN_HISTORY_STATUSES = (
  Object.keys(B2bCampaignStatusTabMapping) as B2bCampaignStatus[]
).filter((status) => B2bCampaignStatusTabMapping[status] === 'history');

const B2bCampaignInProgressStatusesEditableBoExternal: B2bCampaignStatus[] = [
  'campaign_published',
  'campaign_published_failed',
];

export const isB2bCampaignEditableBoExternal = (status: B2bCampaignStatus | null): boolean =>
  !isB2bCampaignInProgress(status) ||
  B2bCampaignInProgressStatusesEditableBoExternal.includes(status as B2bCampaignStatus);

/**
 * failed statuses in which the internal BO can still change the payment method of a started
 * campaign, every in progress step up to the fund request creation, as the payment method is only
 * read when the fund request is created
 */
const B2bCampaignPaymentMethodEditableFailedStatuses: B2bCampaignStatus[] = [
  'campaign_started_failed',
  'campaign_ending_grace_period_failed',
  'campaign_unused_vacation_days_cash_conversion_failed',
  'campaign_ended_failed',
  'campaign_company_allocation_choices_processed_failed',
  'campaign_investment_amount_failed',
  'company_funds_await_failed',
];

export const isB2bCampaignPaymentMethodEditableFailedStatus = (
  status: B2bCampaignStatus | null,
): boolean => status !== null && B2bCampaignPaymentMethodEditableFailedStatuses.includes(status);

export const isCetPendingCashAmounts = (campaign: {
  type: B2bCampaignType;
  state: B2bCampaignStatus | null;
}) => campaign.type === 'unused_vacation_days' && campaign.state === 'campaign_ending_grace_period';

/**
 * a started, non draft, campaign whose employee choices are still being collected by the
 * distributor, which can still be edited
 */
export const isOngoingConsultationCampaignEdit = (campaign: {
  choice_collected_by: B2bCampaignActionHandler | null;
  type: B2bCampaignType;
  start_date: Date | string | null;
  end_date: Date | string | null;
  is_draft: boolean | null;
}): boolean =>
  campaign.choice_collected_by === 'distributor' &&
  !!campaign.start_date &&
  isAfter(new Date(), campaign.start_date) &&
  !!campaign.end_date &&
  isAfter(campaign.end_date, new Date()) &&
  !campaign.is_draft;

/**
 * a campaign failing during its consultation keeps its ongoing consultation edits, which already
 * cover the payment method, except CET whose ongoing edit is limited to the consultation period
 */
export const isPaymentMethodOnlyEdit = (
  campaign: Parameters<typeof isOngoingConsultationCampaignEdit>[0] & {
    savings_funds_request_id: number | null;
    state: B2bCampaignStatus | null;
  },
): boolean =>
  G.isNotNullable(campaign.savings_funds_request_id) ||
  (isB2bCampaignPaymentMethodEditableFailedStatus(campaign.state) &&
    (campaign.type === 'unused_vacation_days' || !isOngoingConsultationCampaignEdit(campaign)));

export const B2bCampaignTypeValues = [
  'participation',
  'profit_sharing',
  'value_sharing_bonus',
  'voluntary_deposit',
  'unused_vacation_days',
  'ccb_matured_election',
  'withdrawal_at_maturity',
  'matching_contribution',
] as const;

export const ZodB2bCampaignType = z.enum(B2bCampaignTypeValues);
export type B2bCampaignType = z.TypeOf<typeof ZodB2bCampaignType>;

export const B2bCampaignReasonValues = [
  'standard',
  'adjustment',
  'regularization',
  'exceptional',
  'omission',
] as const;
export const ZodB2bCampaignReason = z.enum(B2bCampaignReasonValues);
export type B2bCampaignReason = z.TypeOf<typeof ZodB2bCampaignReason>;

// Outside of matching_contribution, a related campaign can only be linked when the campaign corrects
// another one, i.e. for these reasons.
export const B2bRelatedCampaignCorrectionReasons = [
  'adjustment',
  'regularization',
  'omission',
] as const satisfies readonly B2bCampaignReason[];

export const ValueSharingBonusFiscalRuleValues = [
  'with_csg_crds_deduction',
  'exempt_from_csg_crds',
  'without_csg_crds_deduction',
] as const;
export const ZodValueSharingBonusFiscalRule = z.enum(ValueSharingBonusFiscalRuleValues);
export type ValueSharingBonusFiscalRule = z.TypeOf<typeof ZodValueSharingBonusFiscalRule>;

const ZodCampaignAmountType = z.enum(['net', 'gross']);
export type CampaignAmountType = z.TypeOf<typeof ZodCampaignAmountType>;

const ZodGetB2bCampaignOneResponse = z.object({
  id: ZodId,
  company_id: ZodId,
  start_date: ZodDateTimeString.nullable(),
  end_date: ZodDateTimeString.nullable(),
  employees_count: z.number().int().nullable(),
  total_campaign_amount_gross: z.number().int().nullable(),
  total_campaign_amount_net: z.number().int().nullable(),
  total_entry_fee_amount: z.number().int().nullable(),
  total_net_invested_amount: z.number().int().nullable(),
  total_net_amount: z.number().int().nullable(),
  total_gross_amount: z.number().int().nullable(),
  status: ZodB2bCampaignStatus.nullable(),
  savings_funds_request_id: ZodId.nullable(),
  additional_savings_funds_requests: z
    .array(
      z.object({
        id: ZodId,
        is_marked_as_paid: z.boolean().nullable(),
        is_marked_as_completed: z.boolean().nullable(),
        is_marked_as_cancelled: z.boolean().nullable(),
        total_net_amount: z.number().int().nullable(),
      }),
    )
    .nullable(),
  type: ZodB2bCampaignType,
  reason: ZodB2bCampaignReason.nullable(),
  reference_year: z.number().int().nullable(),
  b2b_contract_ids: ZodId.array().min(1),
  b2b_contract_types: z.array(ZodB2bContractType),
  payment_method: ZodB2bCampaignPaymentMethod.nullable(),
  choice_collected_by: ZodB2bCampaignActionHandler.nullable(),
  payout_by: ZodB2bCampaignActionHandler.nullable(),
  allocation_computed_by: ZodB2bCampaignActionHandler.nullable(),
  matching_contribution_computed_by: ZodB2bCampaignActionHandler.nullable(),
  fiscal_period_start: ZodDateString.nullable(),
  fiscal_period_end: ZodDateString.nullable(),
  amount_type_input: ZodCampaignAmountType.nullable(),
  value_sharing_bonus_fiscal_rule: ZodValueSharingBonusFiscalRule.nullable(),
  created_at: z.date().nullable(),
  updated_at: z.date().nullable(),
  is_draft: z.boolean().nullable(),
  is_cancelled: z.boolean().nullable(),
  amount_input: z.number().int().nullable(),
  is_late_payment_interests: z.boolean().nullable(),
  is_emails_enabled: z.boolean().nullable(),
  distribution_rule_file_url: ZodTrimmedString.nullable(),
  allocation_file_url: ZodTrimmedString.nullable(),
  late_payment_interests_override_amount: z.number().int().nullable(),
  total_matching_contribution_amount: z.number().int(),
  total_matching_contribution_amount_gross: z.number().int(),
  total_matching_contribution_entry_fee_amount: z.number().int(),
  total_matching_contribution_net_invested_amount: z.number().int(),
  products: ZodTrimmedString.array().nullable(),
  savings_funds_request_is_marked_as_paid: z.boolean().nullable(),
  savings_funds_request_is_marked_as_completed: z.boolean().nullable(),
  savings_funds_request_is_marked_as_cancelled: z.boolean().nullable(),
  savings_funds_request_total_net_amount: z.number().int().nullable(),
  group_operation_gross_amount: z.number().int().nullable(),
  matching_contribution_related_cash_source: ZodB2bCashSource.nullable(),
  related_campaign_id: ZodId.nullable(),
  /**
   * Human readable reason why the payment method cannot be changed right now, `null` when it can.
   *
   * question: maybe we should send a key that can be translated in the futur
   */
  payment_method_edit_blocker: ZodTrimmedString.nullable(),
});
export type GetB2bCampaignOneResponse = z.infer<typeof ZodGetB2bCampaignOneResponse>;
export const getB2bCampaignOneResponseDistributorColumns = [
  'id',
  'company_id',
  'b2b_contract_ids',
  'b2b_contract_types',
  'type',
  'start_date',
  'end_date',
  'created_at',
] as const satisfies (keyof GetB2bCampaignOneResponse)[];

const ZodGetB2bCampaignsResponse = z.array(
  ZodGetB2bCampaignOneResponse.omit({
    total_matching_contribution_amount: true,
    total_matching_contribution_amount_gross: true,
    total_matching_contribution_entry_fee_amount: true,
    total_matching_contribution_net_invested_amount: true,
    total_entry_fee_amount: true,
    total_net_invested_amount: true,
    savings_funds_request_is_marked_as_paid: true,
    savings_funds_request_is_marked_as_completed: true,
    savings_funds_request_is_marked_as_cancelled: true,
    savings_funds_request_total_net_amount: true,
    additional_savings_funds_requests: true,
    payment_method_edit_blocker: true,
  }),
);
export type GetB2bCampaignsResponse = z.TypeOf<typeof ZodGetB2bCampaignsResponse>;

export const getB2bCampaignsSortColumns: (keyof GetB2bCampaignsResponse[number])[] = [
  'id',
  'company_id',
  'start_date',
  'end_date',
  'type',
  'created_at',
  'updated_at',
];

const ZodFilters = z
  .object({
    id: createZodArrayable(ZodId),
    company_id: createZodArrayable(ZodId),
    savings_funds_request_id: createZodArrayable(ZodId),
    type: createZodArrayable(ZodB2bCampaignType),
    value_sharing_bonus_fiscal_rule: createZodArrayable(ZodValueSharingBonusFiscalRule),
    status: createZodArrayable(ZodB2bCampaignStatus),
    b2b_contract_ids: createZodArrayable(ZodId),
    b2b_contract_types: createZodArrayable(ZodB2bContractType),
    start_date: ZodTrimmedString,
    end_date: ZodTrimmedString,
    from_created_at: ZodTrimmedString,
    to_created_at: ZodTrimmedString,
  })
  .partial();
export const getB2bCampaignFiltersDistributor: (keyof z.infer<typeof ZodFilters>)[] = [
  'id',
  'company_id',
  'b2b_contract_ids',
  'b2b_contract_types',
  'type',
  'start_date',
  'end_date',
  'from_created_at',
  'to_created_at',
];

const ZodGetB2bCampaignsQuery = buildZodBoListQueryParsed(ZodFilters, getB2bCampaignsSortColumns, {
  range: [0, 100],
});

export type GetB2bCampaignsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getB2bCampaignsSortColumns)[number]
>;

export const CampaignAllocationChoiceTypeValues = ['payout', 'plan', 'managed_plan'] as const;
const ZodCampaignAllocationChoiceType = z.enum(CampaignAllocationChoiceTypeValues);
export type CampaignAllocationChoiceType = z.TypeOf<typeof ZodCampaignAllocationChoiceType>;

const ZodCampaignAllocationChoice = z.object({
  amount: z.number().int(),
  plan_id: ZodId.optional().nullable(),
  allocation_type: ZodCampaignAllocationChoiceType,
  deductibility: ZodB2bSavingPlanDeductibility.optional().nullable(),
  instrument_id: ZodId.optional().nullable(),
  risk_profile: ZodEmployeeSavingsRiskProfile.optional().nullable(),
  matching_contribution_amount: z.number().int().optional().nullable(),
});
const ZodCampaignAllocationChoiceDraft = z.object({
  ...ZodCampaignAllocationChoice.shape,
  id: ZodTrimmedString.optional(),
  amount: z.number().int().optional().nullable(),
  allocation_type: ZodCampaignAllocationChoiceType.optional().nullable(),
});
export type CampaignAllocationChoiceDraft = z.infer<typeof ZodCampaignAllocationChoiceDraft>;

const ZodGetB2bCampaignAllocationOneResponse = z.object({
  id: ZodId,
  campaign_id: ZodId,
  company_id: ZodId,
  employee_id: ZodId,
  net_amount: z.number().int().nullable(),
  gross_amount: z.number().int().nullable(),
  invested_amount: z.number().int(),
  entry_fee_amount: z.number().int(),
  net_invested_amount: z.number().int(),
  amount_in_days: ZodAmountInDays.nullable(),
  cet_days: ZodAmountInDays.nullable(),
  rtt_days: ZodAmountInDays.nullable(),
  overtime_days: ZodAmountInDays.nullable(),
  paid_leave_days: ZodAmountInDays.nullable(),
  seniority_leave_days: ZodAmountInDays.nullable(),
  untaken_rest_days: ZodAmountInDays.nullable(),
  cet_amount: z.number().int().nullable(),
  rtt_amount: z.number().int().nullable(),
  overtime_amount: z.number().int().nullable(),
  paid_leave_amount: z.number().int().nullable(),
  seniority_leave_amount: z.number().int().nullable(),
  untaken_rest_amount: z.number().int().nullable(),
  invested_amount_in_days: ZodAmountInDays.nullable(),
  invested_cet_days: ZodAmountInDays.nullable(),
  invested_rtt_days: ZodAmountInDays.nullable(),
  invested_overtime_days: ZodAmountInDays.nullable(),
  invested_paid_leave_days: ZodAmountInDays.nullable(),
  invested_seniority_leave_days: ZodAmountInDays.nullable(),
  invested_untaken_rest_days: ZodAmountInDays.nullable(),
  matching_contribution_amount: z.number().int(),
  payout_amount: z.number().int(),
  late_interests_amount: z.number().int(),
  identifier: ZodTrimmedString.nullable(),
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
  registration_number: ZodTrimmedString.nullable(),
  csg_amount: z.number().int().nullable(),
  crds_amount: z.number().int().nullable(),
  social_status: ZodTrimmedString.nullable(),
  tax_residence_code: ZodTaxResidenceCode.nullable(),
  company_collected_choices: z.array(
    z.object({
      amount: z.number().int().nullable(),
      gross_amount: z.number().int().nullable(),
      csg_amount: z.number().int().nullable(),
      crds_amount: z.number().int().nullable(),
      matching_contribution_amount: z.number().int().nullable(),
      matching_contribution_gross_amount: z.number().int().nullable(),
      plan_id: ZodId.nullable(),
      plan_type: ZodB2bSavingPlanType.nullable(),
      plan_label: ZodTrimmedString.nullable(),
      allocation_type: ZodCampaignAllocationChoiceType.nullable(),
      deductibility: ZodB2bSavingPlanDeductibility.nullable(),
      instrument_id: ZodId.nullable(),
      risk_profile: ZodEmployeeSavingsRiskProfile.nullable(),
      instrument_name: ZodTrimmedString.nullable(),
    }),
  ),
});
export type GetB2bCampaignAllocationOneResponse = z.TypeOf<
  typeof ZodGetB2bCampaignAllocationOneResponse
>;
const ZodGetB2bCampaignAllocationsResponse = z.array(ZodGetB2bCampaignAllocationOneResponse);
export type GetB2bCampaignAllocationsResponse = z.TypeOf<
  typeof ZodGetB2bCampaignAllocationsResponse
>;

export const getB2bCampaignAllocationsSortColumns: (keyof GetB2bCampaignAllocationOneResponse)[] = [
  'employee_id',
  'net_amount',
  'gross_amount',
  'entry_fee_amount',
  'net_invested_amount',
  'firstname',
  'lastname',
  'identifier',
  'social_status',
];

const ZodFiltersGetB2bCampaignAllocation = ZodGetB2bCampaignAllocationOneResponse.pick({
  campaign_id: true,
  employee_id: true,
  net_amount: true,
  gross_amount: true,
  company_id: true,
  social_status: true,
}).partial();

const ZodGetB2bCampaignAllocationsQuery = buildZodBoListQueryParsed(
  ZodFiltersGetB2bCampaignAllocation,
  getB2bCampaignAllocationsSortColumns,
  { range: [0, 10000] },
);

export type GetB2bCampaignAllocationsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFiltersGetB2bCampaignAllocation>,
  (typeof getB2bCampaignAllocationsSortColumns)[number]
>;

export const getB2bCampaignsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bCampaignsQuery,
  responseBody: ZodGetB2bCampaignsResponse,
});
export type GetB2bCampaignsEndpoint = GetEndpointType<typeof getB2bCampaignsEndpoint>;

export const getB2bCampaignsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bCampaignOneResponse,
});
export type GetB2bCampaignsIdEndpoint = GetEndpointType<typeof getB2bCampaignsIdEndpoint>;

export const getB2bCampaignAllocationsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bCampaignAllocationsQuery,
  responseBody: ZodGetB2bCampaignAllocationsResponse,
});
export type GetB2bCampaignAllocationsEndpoint = GetEndpointType<
  typeof getB2bCampaignAllocationsEndpoint
>;

/** The allocations of a campaign, one row per choice, in the branded Excel template */
export const ZodCampaignAllocationsExportBody = z.object({
  kind: z.literal('campaign_allocations'),
  campaign_id: ZodId,
});
export type CampaignAllocationsExportBody = z.infer<typeof ZodCampaignAllocationsExportBody>;

export const postB2bCampaignAllocationsExportEndpoint = endpoint({
  method: 'POST',
  body: ZodCampaignAllocationsExportBody,
  responseBody: ZodBoExportResponse,
});
export type PostB2bCampaignAllocationsExportEndpoint = GetEndpointType<
  typeof postB2bCampaignAllocationsExportEndpoint
>;

/**
 * The employees a sample file is prefilled with: none (an empty file, whose parameters sheet is
 * still filled for choices collected by the company), the current ones, or all of them
 */
export const CampaignSampleEmployeesValues = ['none', 'current', 'all'] as const;
export const ZodCampaignSampleEmployees = z.enum(CampaignSampleEmployeesValues);
export type CampaignSampleEmployees = z.infer<typeof ZodCampaignSampleEmployees>;

/**
 * The campaign wizard's sample files, prefilled with the company's employees. The campaign may
 * not exist yet: they are built from the wizard's values.
 */
export const ZodCampaignSampleExportBody = z.discriminatedUnion('variant', [
  z.object({
    kind: z.literal('campaign_sample'),
    variant: z.literal('distribution_rule'),
    company_id: ZodId,
    contract_ids: z.array(ZodId),
    employees: ZodCampaignSampleEmployees.exclude(['none']),
  }),
  z.object({
    kind: z.literal('campaign_sample'),
    variant: z.literal('allocations'),
    company_id: ZodId,
    contract_ids: z.array(ZodId),
    employees: ZodCampaignSampleEmployees,
    campaign_type: ZodB2bCampaignType,
    amount_type: ZodCampaignAmountType,
    choice_collected_by: ZodB2bCampaignActionHandler,
    matching_contribution_computed_by: ZodB2bCampaignActionHandler.nullable(),
    matching_contribution_related_cash_source: ZodB2bCashSource.nullable(),
  }),
]);
export type CampaignSampleExportBody = z.infer<typeof ZodCampaignSampleExportBody>;

/** The days the current employees invested in a vacation days campaign, to upload amounts for */
export const ZodVacationDaysAllocationsSampleExportBody = z.object({
  kind: z.literal('vacation_days_allocations_sample'),
  company_id: ZodId,
  contract_ids: z.array(ZodId),
  campaign_id: ZodId.nullable(),
});
export type VacationDaysAllocationsSampleExportBody = z.infer<
  typeof ZodVacationDaysAllocationsSampleExportBody
>;

export const postB2bCampaignsExportEndpoint = endpoint({
  method: 'POST',
  body: z.discriminatedUnion('kind', [
    ZodCampaignSampleExportBody,
    ZodVacationDaysAllocationsSampleExportBody,
  ]),
  responseBody: ZodBoExportResponse,
});
export type PostB2bCampaignsExportEndpoint = GetEndpointType<typeof postB2bCampaignsExportEndpoint>;

const ZodPostCampaignsDetailedReportingBody = z.object({
  campaign_id: ZodId,
});
const ZodPostCampaignsDetailedReportingResponse = z.object({
  id: ZodId,
  file_name: ZodTrimmedString,
  file_content_base64: ZodTrimmedString,
  row_count: z.number().int(),
});

export const postCampaignsDetailedReportingEndpoint = endpoint({
  method: 'POST',
  body: ZodPostCampaignsDetailedReportingBody,
  responseBody: ZodPostCampaignsDetailedReportingResponse,
});
export type PostCampaignsDetailedReportingEndpoint = GetEndpointType<
  typeof postCampaignsDetailedReportingEndpoint
>;

const ZodCampaignAllocation = z.object({
  employee_id: ZodId,
  amount: z.number().int(),
  cet_days: ZodAmountInDays.nullable().optional(),
  amount_in_days: ZodAmountInDays.nullable()
    .optional()
    .meta({ deprecated: true, description: 'Deprecated, use cet_days' }),
  rtt_days: ZodAmountInDays.nullable().optional(),
  overtime_days: ZodAmountInDays.nullable().optional(),
  paid_leave_days: ZodAmountInDays.nullable().optional(),
  seniority_leave_days: ZodAmountInDays.nullable().optional(),
  untaken_rest_days: ZodAmountInDays.nullable().optional(),
  // For days allocations, `amount` must equal the sum of the per-day-type amounts below.
  amount_cet: z.number().int().nullable().optional(),
  amount_rtt: z.number().int().nullable().optional(),
  amount_overtime: z.number().int().nullable().optional(),
  amount_paid_leave: z.number().int().nullable().optional(),
  amount_seniority_leave: z.number().int().nullable().optional(),
  amount_untaken_rest: z.number().int().nullable().optional(),
  company_collected_choices: z.array(ZodCampaignAllocationChoice).optional().nullable(),
});

const ZodCampaignAllocationDraft = z.object({
  ...ZodCampaignAllocation.shape,
  amount: z.number().int().optional(),
  company_collected_choices: z.array(ZodCampaignAllocationChoiceDraft).optional().nullable(),
});
export type CampaignAllocationDraft = z.infer<typeof ZodCampaignAllocationDraft>;

const moveAmountInDaysToCetDays = (allocation: unknown) => {
  if (!G.isObject(allocation) || !('amount_in_days' in allocation)) {
    return allocation;
  }
  const { amount_in_days: amountInDays, ...rest }: Record<string, unknown> = allocation;
  return { ...rest, cet_days: rest.cet_days ?? amountInDays };
};

const withCetDays = <T extends z.ZodType>(schema: T) =>
  z.preprocess((allocation: z.input<T>) => moveAmountInDaysToCetDays(allocation), schema);

const ZodCampaignAllocations = z.array(withCetDays(ZodCampaignAllocation)).min(1);
export type CampaignAllocations = z.infer<typeof ZodCampaignAllocations>;

const ZodCampaignAllocationsDraft = z.array(withCetDays(ZodCampaignAllocationDraft)).optional();
export type CampaignAllocationsDraft = z.infer<typeof ZodCampaignAllocationsDraft>;

// Draft allocations as they come from the BO before employees have been matched to the
// uploaded file (e.g. "save for later" right after the allocation file upload). The
// employee may only be referenced through its nir/ntt; the employee_id is resolved
// server-side and rows that cannot be matched are skipped.
const ZodCampaignAllocationDraftInput = z.object({
  ...ZodCampaignAllocationDraft.shape,
  employee_id: ZodId.nullable().optional(),
  nir: ZodTrimmedString.nullable().optional(),
  ntt: ZodTrimmedString.nullable().optional(),
});
export type CampaignAllocationDraftInput = z.infer<typeof ZodCampaignAllocationDraftInput>;

const ZodCampaignAllocationsDraftInput = z
  .array(withCetDays(ZodCampaignAllocationDraftInput))
  .optional();
export type CampaignAllocationsDraftInput = z.infer<typeof ZodCampaignAllocationsDraftInput>;

const ZodCampaignBaseFields = z.object({
  start_date: ZodDate.nullable().optional(),
  end_date: ZodDate.nullable().optional(),
  fiscal_period_start: ZodDate.nullable(),
  fiscal_period_end: ZodDate.nullable(),
  allocations: ZodCampaignAllocations,
  reason: ZodB2bCampaignReason,
  reference_year: z.number().int().nullable().optional(),
  // Processing options: a BO Internal request may override these away from the contract-derived /
  // currently-persisted value (any reason), but only while the campaign hasn't started yet
  // (draft, or published but not yet started). A BO External request passing a different value is
  // rejected with an error; once the campaign has started, any override attempt (from anyone) is
  // silently ignored instead.
  choice_collected_by: ZodB2bCampaignActionHandler.nullable().optional(),
  payout_by: ZodB2bCampaignActionHandler.nullable().optional(),
  allocation_computed_by: ZodB2bCampaignActionHandler.nullable().optional(),
  payment_method: ZodB2bCampaignPaymentMethod.optional().nullable(),
  new_sepa_rib_doc_file: ZodTrimmedString.nullable(),
  amount_type_input: z.enum(['net', 'gross']),
  amount_input: z.number().int().nullable().optional(),
  matching_contribution_related_cash_source: ZodB2bCashSource.nullable().optional(),
  is_unilateral_matching_contribution: z.boolean().optional(),
  related_campaign_id: ZodId.nullable().optional(),
  is_late_payment_interests: z.boolean(),
  is_emails_enabled: z.boolean().optional(),
  late_payment_interests_override_amount: z.number().int().nullable().optional(),
  allocation_computation_status: z.enum(['to_compute', 'computed', 'confirmed']).optional(),
  distribution_rule_file: z
    .object({
      presigned_path: ZodTrimmedString,
      name: ZodTrimmedString,
    })
    .nullable()
    .optional(),
  allocations_file: z
    .object({
      presigned_path: ZodTrimmedString,
      name: ZodTrimmedString,
    })
    .nullable()
    .optional(),
  value_sharing_bonus_fiscal_rule: ZodValueSharingBonusFiscalRule.optional(),
  group_operation_gross_amount: z.number().int().nullable().optional(),
});

export const ZodCampaignAllocationComputationToCompute = ZodCampaignBaseFields.pick({
  fiscal_period_start: true,
  fiscal_period_end: true,
  reason: true,
  distribution_rule_file: true,
});
export const ZodCampaignAllocationComputationComputed = z.object({
  ...ZodCampaignAllocationComputationToCompute.shape,
  allocations: ZodCampaignAllocations,
});

const ZodPostB2bCampaignBodyBase = z
  .object({
    company_id: ZodId,
    b2b_contract_ids: z.array(ZodId).min(1),
    type: ZodB2bCampaignType,
  })
  .and(
    z.discriminatedUnion('is_draft', [
      z.object({
        ...ZodCampaignBaseFields.partial().shape,
        is_draft: z.literal(true),
        allocations: ZodCampaignAllocationsDraftInput,
      }),
      z.object({ ...ZodCampaignBaseFields.shape, is_draft: z.literal(false) }),
    ]),
  )
  .refine(
    (val) =>
      val.type !== 'voluntary_deposit' ||
      (G.isNullable(val.fiscal_period_start) && G.isNullable(val.fiscal_period_end)),
    'Campaign for company voluntary deposits should not have a fiscal period.',
  )
  .refine(
    (val) =>
      B2bCampaignTypePersonalAssetsGroup.includes(val.type) ||
      val.is_unilateral_matching_contribution === true ||
      (G.isNotNullable(val.fiscal_period_start) && G.isNotNullable(val.fiscal_period_end)),
    'Campaign should have a fiscal period.',
  )
  .refine(
    (val) =>
      val.type !== 'value_sharing_bonus' || G.isNotNullable(val.value_sharing_bonus_fiscal_rule),
    'value_sharing_bonus_fiscal_rule is required when type is value_sharing_bonus',
  )
  .refine(
    (val) =>
      val.type === 'value_sharing_bonus' || G.isNullable(val.value_sharing_bonus_fiscal_rule),
    'value_sharing_bonus_fiscal_rule should only be set for value_sharing_bonus campaigns',
  )
  .refine(
    (val) =>
      !val.start_date ||
      !val.end_date ||
      isAfter(val.end_date, val.start_date) ||
      isEqual(val.end_date, val.start_date),
    'End date must be after or equal to start date',
  )
  .refine(
    (val) =>
      val.type !== 'matching_contribution' ||
      G.isNotNullable(val.matching_contribution_related_cash_source) ||
      val.is_unilateral_matching_contribution === true,
    'On matching_contribution campaign, cash source related should be defined.',
  )
  .refine(
    (val) =>
      val.is_unilateral_matching_contribution !== true ||
      (val.type === 'matching_contribution' &&
        G.isNullable(val.matching_contribution_related_cash_source)),
    'Unilateral matching contribution is only allowed on matching_contribution campaign without related cash source.',
  )
  .refine(
    (val) =>
      val.is_draft ||
      G.isNullable(val.related_campaign_id) ||
      val.type === 'matching_contribution' ||
      (B2bRelatedCampaignCorrectionReasons as readonly B2bCampaignReason[]).includes(val.reason),
    'related_campaign_id can only be set on matching_contribution campaigns or on adjustment/regularization/omission corrections.',
  );

const refineReferenceYear = (
  val: {
    reason?: B2bCampaignReason;
    reference_year?: number | null;
    is_unilateral_matching_contribution?: boolean;
  },
  ctx: RefinementCtx,
) => {
  const isUnilateral = val.is_unilateral_matching_contribution === true;

  if (val.reason === 'standard' && !isUnilateral && G.isNotNullable(val.reference_year)) {
    ctx.addIssue({
      code: 'custom',
      message: 'reference_year must be empty when reason is standard',
      path: ['reference_year'],
    });
  }

  if (val.reason !== 'standard' && G.isNullable(val.reference_year)) {
    ctx.addIssue({
      code: 'custom',
      message: 'reference_year is required when reason is not standard',
      path: ['reference_year'],
    });
  }

  if (val.reason === 'standard' && isUnilateral && G.isNullable(val.reference_year)) {
    ctx.addIssue({
      code: 'custom',
      message: 'reference_year is required for unilateral matching contribution',
      path: ['reference_year'],
    });
  }
};

const refineCampaignBody = (
  val: z.infer<typeof ZodPostB2bCampaignBodyBase> | z.infer<typeof ZodPutB2bCampaignBodyBase>,
  ctx: RefinementCtx,
) => {
  if (val.is_draft) {
    // Campaign is draft, ignore further validation
    return;
  }

  refineReferenceYear(val, ctx);

  if (G.isNotNullable(val.fiscal_period_start) && G.isNotNullable(val.fiscal_period_end)) {
    if (val.fiscal_period_start >= val.fiscal_period_end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reference fiscal period Start date must be inferior than End date',
      });
    }

    if (val.fiscal_period_end > new Date()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Reference fiscal period End date cannot be in the future',
      });
    }
  }

  if (val.payment_method !== 'sepa' && val.new_sepa_rib_doc_file !== null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'SEPA RIB doc only allowed when SEPA payment method',
    });
  }
};

const ZodPostB2bCampaignBody = ZodPostB2bCampaignBodyBase.superRefine(refineCampaignBody);

const ZodPostB2bCampaignResponse = z.object({
  id: ZodId,
});

export const postCampaignsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bCampaignBody,
  responseBody: ZodPostB2bCampaignResponse,
});

export type PostCampaignsEndpoint = GetEndpointType<typeof postCampaignsEndpoint>;

export const ZodPutB2bCampaignBodyBase = z.discriminatedUnion('is_draft', [
  z.object({
    ...ZodCampaignBaseFields.partial().shape,
    is_draft: z.literal(true),
    allocations: ZodCampaignAllocationsDraftInput,
  }),
  z.object({
    ...ZodCampaignBaseFields.omit({ amount_type_input: true }).shape,
    is_draft: z.literal(false),
    // Unlike on creation, an edit may legitimately carry no allocation: the payment method of a
    // campaign whose fund request is already created is changed without the allocations ever being
    // loaded. An empty list leaves the allocations untouched.
    allocations: z.array(withCetDays(ZodCampaignAllocation)),
  }),
]);

const ZodPutB2bCampaignBody = ZodPutB2bCampaignBodyBase.superRefine(refineCampaignBody);

export type PutB2bCampaignBody = z.infer<typeof ZodPutB2bCampaignBody>;

export const putB2bCampaignEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutB2bCampaignBody,
  responseBody: ZodPostB2bCampaignResponse,
});

export type PutB2bCampaignEndpoint = GetEndpointType<typeof putB2bCampaignEndpoint>;

export const putB2bCampaignRefreshEndpoint = endpoint({
  method: 'PUT',
  pathParams: createZodParamsValidator(
    z.object({
      id: ZodId,
    }),
  ),
  responseBody: z.object({
    id: ZodId,
  }),
});

export type PutB2bCampaignRefreshEndpoint = GetEndpointType<typeof putB2bCampaignRefreshEndpoint>;

export const deleteCampaignsEndpoint = endpoint({
  method: 'DELETE',
  pathParams: ZodIDPathParam,
  responseBody: z.object({
    id: ZodId,
  }),
});

export type DeleteCampaignsEndpoint = GetEndpointType<typeof deleteCampaignsEndpoint>;

const ZodGetCampaignsEmployeesAwaitingResponseResponseBodyOne = z.object({
  id: ZodId,
  b2b_campaign_id: ZodId,
  company_id: ZodId,
  firstname: ZodTrimmedString.nullable(),
  lastname: ZodTrimmedString.nullable(),
});

type ZodGetCampaignsEmployeesAwaitingResponseResponseBodyOne = z.TypeOf<
  typeof ZodGetCampaignsEmployeesAwaitingResponseResponseBodyOne
>;

export const getB2bCampaignsEmployeesAwaitingResponseSortColumns: (keyof ZodGetCampaignsEmployeesAwaitingResponseResponseBodyOne)[] =
  ['id'];

const ZodFiltersGetB2bCampaignsEmployeesAwaitingResponse =
  ZodGetCampaignsEmployeesAwaitingResponseResponseBodyOne.pick({
    id: true,
    b2b_campaign_id: true,
    company_id: true,
  }).partial();

const ZodGetB2bCampaignsEmployeesAwaitingResponseQuery = buildZodBoListQueryParsed(
  ZodFiltersGetB2bCampaignsEmployeesAwaitingResponse,
  getB2bCampaignsEmployeesAwaitingResponseSortColumns,
  { range: [0, 100] },
);

export type GetB2bCampaignsEmployeesAwaitingResponseQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFiltersGetB2bCampaignsEmployeesAwaitingResponse>,
  (typeof getB2bCampaignsEmployeesAwaitingResponseSortColumns)[number]
>;

const ZodGetCampaignsEmployeesAwaitingResponseResponseBody = z.array(
  ZodGetCampaignsEmployeesAwaitingResponseResponseBodyOne,
);

export const getCampaignsEmployeesAwaitingResponseEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bCampaignsEmployeesAwaitingResponseQuery,
  responseBody: ZodGetCampaignsEmployeesAwaitingResponseResponseBody,
});

export type GetCampaignsEmployeesAwaitingResponseEndpoint = GetEndpointType<
  typeof getCampaignsEmployeesAwaitingResponseEndpoint
>;

const ZodCalculateTotalMatchingContributionsBody = z
  .object({
    company_id: ZodId,
    type: ZodB2bCampaignType,
    allocations: ZodCampaignAllocationsDraft,
    amount_type_input: ZodCampaignAmountType,
    matching_contribution_computed_by: ZodB2bCampaignActionHandler.nullable(),
    matching_contribution_related_cash_source: ZodB2bCashSource.optional().nullable(),
    is_unilateral_matching_contribution: z.boolean().optional(),
    reason: ZodB2bCampaignReason,
    fiscal_period_start: ZodDateString.nullable(),
    reference_year: z.number().int().nullable().optional(),
    value_sharing_bonus_fiscal_rule: ZodValueSharingBonusFiscalRule.optional().nullable(),
  })
  .refine(
    (val) =>
      val.type !== 'matching_contribution' ||
      G.isNotNullable(val.matching_contribution_related_cash_source) ||
      val.is_unilateral_matching_contribution === true,
    'When calculating total MC on matching_contribution campaign, please provide the related cash source.',
  )
  .refine(
    (val) =>
      val.type !== 'value_sharing_bonus' || G.isNotNullable(val.value_sharing_bonus_fiscal_rule),
    'value_sharing_bonus_fiscal_rule is required when type is value_sharing_bonus',
  )
  .refine(
    (val) =>
      val.type === 'value_sharing_bonus' || G.isNullable(val.value_sharing_bonus_fiscal_rule),
    'value_sharing_bonus_fiscal_rule should only be set for value_sharing_bonus campaigns',
  )
  .superRefine((val, ctx) => {
    refineReferenceYear(val, ctx);
  });

export const postCalculateMatchingContributionsEndpoint = endpoint({
  method: 'POST',
  body: ZodCalculateTotalMatchingContributionsBody,
  responseBody: z.object({
    id: ZodId,
    total_matching_contributions: z.number().int(),
    matching_contributions: z.record(
      ZodTrimmedString,
      z.object({
        total: z.number().int(),
        choices: z.array(
          z.object({
            id: ZodTrimmedString.optional(),
            // amount is always the net MC, whatever amount_type_input is, and taxes_amount the
            // company-paid CSG/CRDS on top of it, so gross = amount + taxes_amount. Every MC
            // ceiling is gross, so amount_to_maximize is too — and it is the amount left for the
            // whole plan category, not for this choice alone.
            amount: z.number().int(),
            taxes_amount: z.number().int(),
            amount_to_maximize: z.number().int(),
            max_amount: z.number().int(),
          }),
        ),
      }),
    ),
    // Company-provided matching contributions going over what the employee may still receive for
    // the plan category. Always empty unless the company provides the amounts itself.
    ceiling_overruns: z.array(
      z.object({
        employee_id: ZodId,
        saving_plan_category: ZodB2bSavingPlanCategory,
        exceeded_by: z.number().int(),
      }),
    ),
  }),
});

export type PostCalculateMatchingContributionsEndpoint = GetEndpointType<
  typeof postCalculateMatchingContributionsEndpoint
>;
